/**
 * The renew decision — moved from the app shell (sdk ADR-010), where it lived beside the DRIVER because that is where the driver
 * was. The driver moved here; the decision moves with it, because co-locating
 * it with `classifyVerdict` is what makes the fleet-logout shape structural:
 * the renew routine has no access to a sign-out call at all. It can only apply
 * an event and back off.
 */
import { classifyVerdict } from '../auth/authState.js';
import type { HolderEvent, SessionVerdict } from '../auth/authState.js';

export const RENEW_DEBOUNCE_MS = 5 * 60 * 1000;
export const RENEW_BASE_BACKOFF_MS = 15 * 1000;
export const RENEW_MAX_BACKOFF_MS = 5 * 60 * 1000;

/** The daemon-arm mirror backstop's own cadence — the frame stream is the
 *  primary path (sdk ADR-019), and the daemon's own renew loop runs at 60s, so
 *  polling faster buys nothing. */
export const DAEMON_MIRROR_INTERVAL_MS = 60_000;

/** (sdk ADR-018) */
export const BROWSER_REFRESH_LEAD_MS = 100_000;

/** (sdk ADR-018) */
export function isRefreshDue(
  expiresAtSeconds: number,
  leadMs: number,
  nowMs: number,
): boolean {
  return nowMs + leadMs >= expiresAtSeconds * 1000;
}

export interface RenewFailureDecision {
  verdict: SessionVerdict;
  /** The event the driver applies through the reducer. */
  event: HolderEvent;
  /** True only when the credential was GLOBALLY and TERMINALLY rejected. The
   *  SDK never routes on this — it is advisory to the host (an overlay is a
   *  host concern); the SDK's own transition comes from `event`. */
  signOut: boolean;
  retryAfterMs: number;
}

/**
 * Classify one failed renew round-trip.
 *
 * The shape this exists to prevent is measured: `renewSession` proxies Stytch,
 * and when Stytch is degraded the backend answers `503 STYTCH_UNAVAILABLE` — an
 * error that adjudicates NOTHING about anyone's session. Treating that as a
 * sign-out cleared every web tab that was open during the blip.
 * `degraded(unconfirmed)` is the phase that fixes it: material and identity are
 * RETAINED and the driver backs off. Only a `rejected` verdict — bad signature, unknown kid, wrong
 * issuer — reaches `no_credential`, and it gets there through the SDK reducer,
 * not through a `clearAuth()` call sited in a catch block.
 */
export function decideRenewFailure(err: unknown, attempts: number, at: number = Date.now()): RenewFailureDecision {
  const verdict = classifyVerdict(err);
  const event: HolderEvent = { type: 'VerdictObserved', verdict, at };

  if (verdict.verdict === 'unknown') {
    // 5xx / STYTCH_UNAVAILABLE / timeout / transport. Retain everything, back
    // off, try again. `attempts` is the holder's own unconfirmed round-trip
    // counter, so the delay grows across failures and resets on the first 2xx.
    const backoff = RENEW_BASE_BACKOFF_MS * 2 ** Math.max(0, attempts);
    return {
      verdict,
      event,
      signOut: false,
      retryAfterMs: Math.min(RENEW_MAX_BACKOFF_MS, backoff),
    };
  }

  return {
    verdict,
    event,
    // `rejected` and ONLY `rejected`. Note what is deliberately absent:
    // `expired` (renew again — that is the whole point), `not_activated`
    // (activate, material intact), `absent` (we sent nothing; that says nothing
    // about the session).
    signOut: verdict.verdict === 'rejected',
    retryAfterMs: RENEW_DEBOUNCE_MS,
  };
}
